/**
 * OASIS application section registry — the single source of section identity,
 * order, labels, and audience visibility.
 *
 * WHY THIS EXISTS
 *
 * This metadata was forked three ways, and the copies had silently diverged:
 *
 *  - `APPLICATION_SECTIONS` (`projects/oasis/.../application-form.store.ts`) —
 *    the applicant form's navigation order.
 *  - `ApplicationViewComponent.sections` (the external read-only view) — includes
 *    `programEligibility`, excludes `demographics`.
 *  - `OasisDetailComponent.sections` (the internal OARM detail page) — excludes
 *    BOTH `programEligibility` and `demographics`, so reviewers could not see
 *    program eligibility at all. That was a defect, not a policy.
 *
 * `APPLICATION_SECTIONS` still exists and still has to: it carries the form's
 * `order` and its own routing, and a library cannot import from an app. So the
 * LABELS are duplicated across the two, and they drifted — this registry said
 * "Education & Experience" and "Essays" for months after the form nav and the
 * screens themselves had moved to "Education & EPAs" and "Essay", which meant the
 * read-only accordions on BOTH portals disagreed with the form the applicant had
 * just filled in. Fixed Sept 2026 and now pinned by an invariant test in
 * `application-form.store.spec.ts`, which is the only place that can see both.
 * Change a label in one, change it in the other.
 *
 * THE TRAP THIS FILE AVOIDS: `APPLICATION_SECTIONS` keys its first entry
 * `'instructions'` (its ROUTE), while the body field is
 * `instructionsAndAcknowledgements`. Both view pages read `body[section.key]`.
 * Anyone "consolidating" by lifting `APPLICATION_SECTIONS` into shared code
 * therefore breaks that section on both pages with no error — it just renders
 * empty. So `key` here is always the `IOasisApplicationBase` FIELD NAME, and the
 * applicant form's route is carried separately as `route`.
 *
 * NOT SECTIONS: `metadata` (internal bookkeeping — cycle year, OMB number, save
 * progress) and `reviewFlags` (a staff-only summary that belongs in the page
 * header next to the status, not in the body accordion) are both fields on the
 * body but are deliberately absent here.
 */

import type { DisplayAudience } from './oasis-display.types';

export interface OasisSectionDescriptor {
	/**
	 * The `IOasisApplicationBase` field name — NOT the applicant form's route.
	 * This is what the projection uses to look up the section's data.
	 */
	key: string;
	/** Heading shown to the reader. */
	label: string;
	/**
	 * Audiences permitted to see this section at all. A section whose audience is
	 * excluded is absent from the projection entirely — not rendered as empty or
	 * restricted, which would still disclose that the data exists.
	 *
	 * EVERY SECTION MUST INCLUDE `'applicant'`. An applicant is reading their own
	 * application; there is nothing in these sections to keep from them, and an
	 * invariant test enforces it. Narrowing applies to staff only.
	 */
	audiences: readonly DisplayAudience[];
	/** The applicant form's route segment, where one exists. Hosts may ignore it. */
	route?: string;
}

const ALL_AUDIENCES: readonly DisplayAudience[] = ['applicant', 'staff'];

/**
 * Visible to the applicant but not to staff.
 *
 * Note the asymmetry only ever runs in this direction. There is no
 * `STAFF_ONLY` — a section an applicant could not see would be their own
 * information withheld from them, which is not a thing this product does.
 */
const APPLICANT_ONLY: readonly DisplayAudience[] = ['applicant'];

/**
 * Every section of the application body, in reading order.
 *
 * The order follows the applicant's narrative (the order the external view page
 * already used), not the form's `order` field, whose non-contiguous values are
 * an artifact of sections that were retired in the 2026 redesign.
 */
export const OASIS_DISPLAY_SECTIONS: readonly OasisSectionDescriptor[] = [
	{ key: 'instructionsAndAcknowledgements', label: 'Instructions & Acknowledgements', audiences: ALL_AUDIENCES, route: 'instructions' },
	{ key: 'personalInformation', label: 'Personal Information', audiences: ALL_AUDIENCES, route: 'personal-information' },
	{ key: 'citizenship', label: 'Citizenship', audiences: ALL_AUDIENCES, route: 'citizenship' },
	{ key: 'programEligibility', label: 'Program Eligibility', audiences: ALL_AUDIENCES, route: 'program-eligibility' },
	{ key: 'componentsOfInterest', label: 'Components of Interest', audiences: ALL_AUDIENCES, route: 'components-of-interest' },
	{ key: 'educationExperience', label: 'Education & EPAs', audiences: ALL_AUDIENCES, route: 'education-experience' },
	{ key: 'barAdmission', label: 'Bar Admission', audiences: ALL_AUDIENCES, route: 'bar-admission' },
	{ key: 'professionalAccomplishments', label: 'Professional Accomplishments', audiences: ALL_AUDIENCES, route: 'professional-accomplishments' },
	{ key: 'honorsAndAwards', label: 'Honors & Awards', audiences: ALL_AUDIENCES, route: 'honors-awards' },
	{ key: 'employment', label: 'Employment', audiences: ALL_AUDIENCES, route: 'employment' },
	{ key: 'references', label: 'References', audiences: ALL_AUDIENCES, route: 'references' },
	{ key: 'militaryService', label: 'Military Service', audiences: ALL_AUDIENCES, route: 'military-service' },
	{ key: 'essays', label: 'Essay', audiences: ALL_AUDIENCES, route: 'essays' },
	{ key: 'languages', label: 'Languages', audiences: ALL_AUDIENCES, route: 'languages' },
	{ key: 'additionalInformation', label: 'Additional Information', audiences: ALL_AUDIENCES, route: 'additional-information' },
	/**
	 * APPLICANT ONLY, and this is a privacy control rather than a display choice.
	 *
	 * Demographics is voluntary self-identification collected solely for OARM's
	 * aggregate equal-opportunity reporting. Its own type documentation states it
	 * "must NOT be returned to hiring officials, reviewers, or any non-OARM
	 * requester". Excluding it here keeps it out of every staff projection. The
	 * applicant of course still sees it — they wrote it.
	 *
	 * As of Sept 2026 the API is stricter still: `BODY_VISIBILITY` in `body-views.ts`
	 * narrowed `demographics` to `['applicant']`, so it is withheld from OARM as well as
	 * from component reviewers. Aggregate reporting is a separate read path, not this
	 * projection. This entry already matched that outcome, because `DisplayAudience` has
	 * no OARM/component distinction — both are `'staff'`.
	 *
	 * This is defense in depth, NOT the enforcement point. The ADMIN API must not send
	 * the block to a requester who may not see it; a client-side omission protects
	 * nothing if the data already crossed the wire.
	 */
	{ key: 'demographics', label: 'Demographics', audiences: APPLICANT_ONLY, route: 'demographics' },
	{ key: 'supportingDocuments', label: 'Supporting Documents', audiences: ALL_AUDIENCES, route: 'supporting-documents' },
	{ key: 'certifyAndSubmit', label: 'Certify & Submit', audiences: ALL_AUDIENCES, route: 'certify-submit' },
];

/** The sections a given audience is permitted to see, in reading order. */
export function sectionsForAudience(audience: DisplayAudience): readonly OasisSectionDescriptor[] {
	return OASIS_DISPLAY_SECTIONS.filter((section) => section.audiences.includes(audience));
}

/** Look up one descriptor by body field name. */
export function findSectionDescriptor(key: string): OasisSectionDescriptor | undefined {
	return OASIS_DISPLAY_SECTIONS.find((section) => section.key === key);
}
